import React from "react";
import { Dialog, Box, Typography, DialogContent, DialogActions, Button, CircularProgress, useMediaQuery, Divider, Tooltip, Tab, Tabs } from "@mui/material";
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import DescriptionMatchSettings from "./description-match-settings";
import Toggle from "../ui/toggle";
import { Link, Unlink, Database, Link2Off, CheckCircle2 } from "lucide-react";

import useApi from "../../hooks/use-api";
import buildApiRequestConfig from "../../api/url-builder";
import routes from "../../api/routes";
import { useSettings } from "../../providers/settings-provider";
import { usePermissions } from "../../providers/permissions-provider";
import { useUser } from "../../providers/user-provider";
import DeletePopup from "./delete";
import LinkTransactionPopup from "./link-transaction";
import InvoicePreview from "./invoice-preview";
import { useToast } from "../../providers/toaster-provider";
import trxImage from '../../files/images/transaction.png';
import AddProvisionPopup from "./add-provision";
import ProvisionTransactionsDisplay from "../provision-transactions-display";
import theme from "../../util/theme/theme";
import TextToggle from "../ui/text-toggle";

// helper used when provisioning a transaction that isn't recognized as recurring
const adjustStartDate = (dateStr?: string, analysis?: any): string | undefined => {
    if (!dateStr) return dateStr;
    const interval = analysis?.interval?.toString()?.toLowerCase();
    if (interval === 'unknown' || interval === 'not recurring' || interval === 'none') {
        const d = new Date(dateStr);
        d.setMonth(d.getMonth() + 1);
        return d.toISOString().split('T')[0];
    }
    return dateStr;
};

interface TransactionDetailProps {
    open: boolean;
    transactionId: string | null;
    invoiceNumber?: string | null;
    onClose: () => void;
    companyStatus: {
        lowestAmount: number | null;
        highestAmount: number | null;
    };
    onLink?: () => void;
    autoOpenLinkModal?: boolean;
    onAutoOpenLinkModalClose?: () => void;
    // Called when there's no transaction to show (e.g. an invoice opened by
    // invoiceNumber that has no provision) - lets the caller fall back.
    onNotFound?: () => void;
}

const TransactionDetail: React.FC<TransactionDetailProps> = ({
    open,
    transactionId,
    invoiceNumber,
    onClose,
    companyStatus,
    onLink,
    autoOpenLinkModal,
    onAutoOpenLinkModalClose,
    onNotFound,
}) => {
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const rowVirtualizerInstanceRef = React.useRef<any>(null);
    const relatedRowVirtualizerInstanceRef = React.useRef<any>(null);
    // Tracks the last analyzePattern response/error object already handled, so the
    // effect below (which reads/writes pendingDescriptionGuid — one of its own
    // deps) doesn't reprocess the same stale response when it re-runs just
    // because pendingDescriptionGuid changed, which was causing an infinite loop.
    const processedAnalysisRef = React.useRef<any>(null);
    const processedCreateProvisionRef = React.useRef<number | null>(null);
    const processedMarkTrxRef = React.useRef<any>(null);

    const { formatDate, formatAmount } = useSettings();
    const { can } = usePermissions();
    const { user } = useUser();
    const { showError, showSuccess } = useToast();
    const [data, setData] = React.useState<any>(null);
    // data.linkedTransactions includes the transaction's own record (same group
    // the History tab renders), so it must be excluded here too — otherwise this
    // is always truthy and the transaction reads as "already linked" even when
    // nothing else is actually linked to it.
    const hasLinkedTransactions = (data?.linkedTransactions || []).some((trx: any) => trx.guid !== data?.transaction?.guid);
    // Same exclusion as hasLinkedTransactions above, but the actual rows -
    // passed into the Find Matching Transactions popup so it can show what's
    // already linked to this provision/transaction instead of only new
    // candidates, since a provision can be partially matched already.
    const otherLinkedTransactions = React.useMemo(() => (
        (data?.linkedTransactions || []).filter((trx: any) => trx.guid !== data?.transaction?.guid)
    ), [data?.linkedTransactions, data?.transaction?.guid]);
    // Xero invoices that were already PAID at sync time still land as type
    // 'provision' rows (paidAtImport backfill, xero-import-service.js). When
    // one of those has nothing linked to it, showing it with the usual
    // "open, unlinked provision" styling was misleading, so it borrows
    // real-transaction styling instead. But once it IS linked to the real
    // transaction that paid it (xero-import-service.js now links paid
    // invoices to a matching transaction instead of leaving them
    // unreconciled), that styling backfires the other way - it hides the
    // Provision/linked-transaction relationship the History tab exists to
    // show. Only borrow the real-transaction look while genuinely unlinked.
    const isPaidAtImportProvision = data?.transaction?.type === 'provision' && data?.transaction?.sourceData?.paidAtImport === true && !hasLinkedTransactions;
    // Nothing linked yet means the History tab's table would just be this
    // row plus a "No Linked Transactions" placeholder - true from either
    // side of the relationship (an unlinked provision, or a real transaction
    // nothing has been linked to). Show an explanatory empty state with the
    // link action instead of that table.
    const isUnlinked = !!data?.transaction && !hasLinkedTransactions;
    // Linking, unlinking and OPEN/CLOSED change the transaction (or, for a
    // provision occurrence, the provision) - they need that resource's Update
    // permission, not just Read.
    const canChangeLinks = data?.transaction?.type === 'provision' ? can('provisions', 'update') : can('transactions', 'update');
    const [tabValue, setTabValue] = React.useState<number>(0);
    // Xero tab (real transactions) - which candidate invoices are ticked,
    // so their combined amount can be checked against this transaction's own
    // before linking (a single real transaction is often a batch payment
    // covering several invoices at once, not a 1:1 match).
    const [xeroCandidateSelection, setXeroCandidateSelection] = React.useState<Record<string, boolean>>({});
    // Xero Payment tab (provisions) - same idea in reverse: an invoice can
    // legitimately be settled by more than one real transaction (split/
    // partial payments), so this also needs multi-select + a running total,
    // not just a single-click link.
    const [realCandidateSelection, setRealCandidateSelection] = React.useState<Record<string, boolean>>({});
    // Both Xero tabs only make sense when this entity actually has Xero
    // connected - otherwise there's no sourceData.xeroPayments data and no
    // reconciliation to do, so they'd just be a permanently-empty tab.
    // Checked the same way XeroSyncButton does (status === 'Active'),
    // fetched only once the popup is actually open.
    const [fetchXeroIntegration, setFetchXeroIntegration] = useApi();
    const isXeroConnected = fetchXeroIntegration.data?.data?.status === 'Active';
    const [relatedTransactions, setRelatedTransactions] = React.useState<any[]>([]);
    const [openModal, setOpenModal] = React.useState<string>('');
    const [unlinkingTransactionId, setUnlinkingTransactionId] = React.useState<string | null>(null);
    const [isLinkable, setIsLinkable] = React.useState<boolean>(false);
    const [provisionThisData, setProvisionThisData] = React.useState<{ date?: string; amount?: number | string; description?: string } | null>(null);
    const [intervalAnalysis, setIntervalAnalysis] = React.useState<any>(null);
    const [patternAnalysisMessage, setPatternAnalysisMessage] = React.useState<string>('');
    const [showPatternConfirmation, setShowPatternConfirmation] = React.useState(false);

    // description-match settings dialog
    const [showMatchSettings, setShowMatchSettings] = React.useState(false);
    // description match dialog state handled by component
    const [pendingDescriptionGuid, setPendingDescriptionGuid] = React.useState<string | null>(null);
    const [pendingDescriptionText, setPendingDescriptionText] = React.useState<string>('');
    const [descriptionOptions, setDescriptionOptions] = React.useState<{start: number; length: number} | null>(null);
    const [isDescriptionOnlyMatch, setIsDescriptionOnlyMatch] = React.useState(false);
    const [selectedLinkedTransactionId, setSelectedLinkedTransactionId] = React.useState<string | null>(null);
    // A Find Matching candidate row that's actually an invoice-backed
    // provision goes to the invoice PDF preview instead of this popup's own
    // detail modal (see the LinkTransactionPopup's onRowClick below) - the
    // row only has the invoice's invoiceNumber, so it's resolved to the
    // Invoice record's own id first, same lookup invoices.tsx uses.
    const [previewInvoiceNumber, setPreviewInvoiceNumber] = React.useState<string | null>(null);
    const [previewInvoiceId, setPreviewInvoiceId] = React.useState<number | null>(null);
    const [fetchInvoiceByNumber, setFetchInvoiceByNumber] = useApi();
    // Review step for transactions the backend auto-matched + marked while creating
    // the provision - shows exactly what createProvisionTemplate returned as
    // transactionsToMark, so the user can see/confirm what got linked.
    const [showProvisionTransactions, setShowProvisionTransactions] = React.useState(false);
    const [provisionTransactionsToConfirm, setProvisionTransactionsToConfirm] = React.useState<any[]>([]);
    const [createdProvisionIdForMark, setCreatedProvisionIdForMark] = React.useState<number | null>(null);

    const [fetch, setFetch] = useApi();
    const [link, setLink] = useApi();
    const [unlink, setUnlink] = useApi();
    const [createProvision, setCreateProvision] = useApi();
    const [analyzePattern, setAnalyzePattern] = useApi();
    const [fetchRelated, setFetchRelated] = useApi();
    const [fetchPatternAnalysis, setFetchPatternAnalysis] = useApi();
    const [fetchXeroCandidates, setFetchXeroCandidates] = useApi();
    const [fetchRealCandidates, setFetchRealCandidates] = useApi();
    const [markTrx, setMarkTrx] = useApi();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [toggleLinkable, setToggleLinkable] = useApi();

    const relatedTableColumns = React.useMemo<MRT_ColumnDef<any>[]>(
        () => [
            {
                accessorKey: 'date',
                header: 'Date',
                size: 120,
                Cell: ({ cell, row }) => {
                    const color = row.original.type === 'Provision' ? 'var(--color-provision-text)' : 'inherit';
                    const dateLinked = row.original?.dateLinked;
                    return (
                        <Tooltip
                            title={dateLinked ? `Date Linked: ${formatDate(new Date(dateLinked))}` : ''}
                            disableHoverListener={!dateLinked}
                        >
                            <Typography fontSize={isMobile ? 14 : 16} sx={{ color, width: 'fit-content' }}>
                                {formatDate(new Date(cell.getValue<string>()))}
                            </Typography>
                        </Tooltip>
                    );
                },
            },
            {
                accessorKey: 'amount',
                header: 'Amount',
                size: 100,
                Cell: ({ cell, row }) => {
                    const color = row.original.type === 'Provision' ? 'var(--color-provision-text)' : 'inherit';
                    const displayAmount = row.original.type === 'Provision' && row.original.originalAmount !== undefined
                        ? row.original.originalAmount
                        : cell.getValue<number>();
                    return <Typography fontSize={isMobile ? 14 : 16} sx={{ color }}>{formatAmount?.(displayAmount)}</Typography>;
                },
            },
            {
                accessorKey: 'description',
                header: 'Description',
                size: 180,
                Cell: ({ cell, row }) => {
                    const color = row.original.type === 'Provision' ? 'var(--color-provision-text)' : 'inherit';
                    return <Typography fontSize={isMobile ? 14 : 16} sx={{ color }}>{cell.getValue<string>()}</Typography>;
                },
            },
            {
                accessorKey: 'category',
                header: 'Category',
                size: 120,
                enableHiding: true,
                columnDefType: 'display',
                Cell: ({ cell, row }: any) => {
                    const color = row.original.type === 'Provision' ? 'var(--color-provision-text)' : 'inherit';
                    const value = (cell as any).getValue?.() as string;
                    return <Typography fontSize={isMobile ? 14 : 16} sx={{ color }}>{value || '-'}</Typography>;
                },
            },
            {
                accessorKey: 'bank',
                header: 'Bank',
                size: 120,
                enableHiding: true,
                columnDefType: 'display',
                Cell: ({ cell, row }: any) => {
                    const color = row.original.type === 'Provision' ? 'var(--color-provision-text)' : 'inherit';
                    const value = (cell as any).getValue?.() as string;
                    return <Typography fontSize={isMobile ? 14 : 16} sx={{ color }}>{value || '-'}</Typography>;
                },
            },
        ],
        [formatDate, formatAmount, isMobile]
    );

    // Pattern Analysis tab (recurring provisions created via "Provision
    // This" only) - mirrors related-transactions-detail.tsx's own
    // columns/derivation for the same patternAnalysisSnapshot shape, scoped
    // here to a single occurrence's template rather than a template picked
    // from the Provisions page.
    const analysisColumns = React.useMemo<MRT_ColumnDef<any>[]>(
        () => [
            {
                accessorKey: 'date',
                header: 'Date',
                size: 100,
                Cell: ({ cell }) => (
                    <Typography fontSize={isMobile ? 12 : 14}>{formatDate(new Date(cell.getValue<string>()))}</Typography>
                ),
            },
            {
                accessorKey: 'description',
                header: 'Description',
                size: 220,
                Cell: ({ cell }) => (
                    <Typography fontSize={isMobile ? 12 : 14}>{cell.getValue<string>()}</Typography>
                ),
            },
            {
                accessorKey: 'amount',
                header: 'Amount',
                size: 100,
                Cell: ({ cell }) => (
                    <Typography fontSize={isMobile ? 12 : 14}>{formatAmount?.(cell.getValue<number>())}</Typography>
                ),
            },
        ],
        [formatDate, formatAmount, isMobile]
    );

    const xeroCandidatesColumns = React.useMemo<MRT_ColumnDef<any>[]>(
        () => [
            {
                id: 'xeroPaymentGroup',
                header: 'Xero Payment',
                muiTableHeadCellProps: {
                    sx: { backgroundColor: 'var(--color-primary)', color: '#fff' },
                },
                columns: [
                    {
                        id: 'paymentDate',
                        header: 'Date',
                        size: 100,
                        accessorFn: (row: any) => row.matchedPayment?.date,
                        muiTableHeadCellProps: { sx: { backgroundColor: 'rgba(var(--color-primary-rgb), 0.12)', color: 'var(--color-primary)' } },
                        muiTableBodyCellProps: { sx: { backgroundColor: 'rgba(var(--color-primary-rgb), 0.08)' } },
                        Cell: ({ cell }) => (
                            <Typography fontSize={isMobile ? 12 : 14} sx={{ color: 'var(--color-primary)', fontWeight: 500 }}>{formatDate(new Date(cell.getValue<string>()))}</Typography>
                        ),
                    },
                    {
                        id: 'paymentAmount',
                        header: 'Amount',
                        size: 100,
                        accessorFn: (row: any) => row.matchedPayment?.amount,
                        muiTableHeadCellProps: { sx: { backgroundColor: 'rgba(var(--color-primary-rgb), 0.12)', color: 'var(--color-primary)' } },
                        // Amber instead of the usual primary tint when this payment
                        // doesn't cover the invoice's full amount (e.g. $205 paid of a
                        // $240 invoice) - same amber the Invoices page already uses for
                        // "partially linked" (see invoices.tsx), so a partial payment is
                        // visible at a glance instead of only showing up once you add up
                        // the selected total.
                        muiTableBodyCellProps: ({ row }: { row: any }) => {
                            const isPartial = Math.abs((Number(row.original.matchedPayment?.amount) || 0) - (Number(row.original.amount) || 0)) >= 0.005;
                            return { sx: { backgroundColor: isPartial ? 'rgba(245, 158, 11, 0.12)' : 'rgba(var(--color-primary-rgb), 0.08)' } };
                        },
                        Cell: ({ row, cell }) => {
                            const isPartial = Math.abs((Number(row.original.matchedPayment?.amount) || 0) - (Number(row.original.amount) || 0)) >= 0.005;
                            return (
                                <Tooltip title={isPartial ? `Partial payment - invoice amount is ${formatAmount?.(row.original.amount)}` : ''} arrow disableHoverListener={!isPartial}>
                                    <Typography fontSize={isMobile ? 12 : 14} sx={{ color: isPartial ? '#F59E0B' : 'var(--color-primary)', fontWeight: 600 }}>{formatAmount?.(cell.getValue<number>())}</Typography>
                                </Tooltip>
                            );
                        },
                    },
                ],
            },
            {
                id: 'invoiceGroup',
                header: 'Invoice',
                columns: [
                    {
                        accessorKey: 'date',
                        header: 'Date',
                        size: 100,
                        Cell: ({ cell }) => (
                            <Typography fontSize={isMobile ? 12 : 14}>{formatDate(new Date(cell.getValue<string>()))}</Typography>
                        ),
                    },
                    {
                        accessorKey: 'amount',
                        header: 'Amount',
                        size: 100,
                        Cell: ({ cell }) => (
                            <Typography fontSize={isMobile ? 12 : 14}>{formatAmount?.(cell.getValue<number>())}</Typography>
                        ),
                    },
                    {
                        accessorKey: 'description',
                        header: 'Description',
                        size: 220,
                        Cell: ({ cell }) => (
                            <Typography fontSize={isMobile ? 12 : 14} fontWeight={600}>{cell.getValue<string>()}</Typography>
                        ),
                    },
                ],
            },
        ],
        [formatDate, formatAmount, isMobile]
    );

    const realCandidatesColumns = React.useMemo<MRT_ColumnDef<any>[]>(
        () => [
            {
                accessorKey: 'date',
                header: 'Date',
                size: 100,
                Cell: ({ cell }) => (
                    <Typography fontSize={isMobile ? 12 : 14}>{formatDate(new Date(cell.getValue<string>()))}</Typography>
                ),
            },
            {
                accessorKey: 'amount',
                header: 'Amount',
                size: 100,
                Cell: ({ cell }) => (
                    <Typography fontSize={isMobile ? 12 : 14}>{formatAmount?.(cell.getValue<number>())}</Typography>
                ),
            },
            {
                accessorKey: 'description',
                header: 'Description',
                size: 240,
                Cell: ({ cell }) => (
                    <Typography fontSize={isMobile ? 12 : 14} fontWeight={600}>{cell.getValue<string>()}</Typography>
                ),
            },
        ],
        [formatDate, formatAmount, isMobile]
    );

    const patternAnalysis = fetchPatternAnalysis.data?.patternAnalysisSnapshot || null;
    const patternAnalysisSourceTransaction = patternAnalysis?.transactions?.find((t: any) => t.isSource) || null;
    const patternAnalysisMatches = patternAnalysis?.transactions?.filter((t: any) => !t.isSource) || [];

    const tableColumns = React.useMemo<MRT_ColumnDef<any>[]>(
        () => {
            const isViewingProvision = data?.transaction?.type === 'provision' && !isPaidAtImportProvision;
            const columns: (MRT_ColumnDef<any> & { accessorKey?: string })[] = [
                {
                    accessorKey: 'type',
                    header: 'Type',
                    size: 80,
                    enableHiding: true,
                    columnDefType: 'display',
                    Cell: ({ row }: any) => {
                        const type = row.original.type;
                        const color = type === 'Provision' ? 'var(--color-provision-text)' : 'inherit';
                        return <Typography fontSize={isMobile ? 14 : 16} sx={{ color }}>{type}</Typography>;
                    },
                },
                {
                    accessorKey: 'date',
                    header: 'Date',
                    size: 120,
                    Cell: ({ cell, row }) => {
                        const color = row.original.type === 'Provision' ? 'var(--color-provision-text)' : 'inherit';
                        const dateLinked = row.original?.dateLinked;
                        return (
                            <Tooltip
                                title={dateLinked ? `Date Linked: ${formatDate(new Date(dateLinked))}` : ''}
                                disableHoverListener={!dateLinked}
                            >
                                <Typography fontSize={isMobile ? 14 : 16} sx={{ color, width: 'fit-content' }}>
                                    {formatDate(new Date(cell.getValue<string>()))}
                                </Typography>
                            </Tooltip>
                        );
                    },
                },
                {
                    accessorKey: 'amount',
                    header: 'Amount',
                    size: 100,
                    Cell: ({ cell, row }) => {
                        const color = row.original.type === 'Provision' ? 'var(--color-provision-text)' : 'inherit';
                        // For provisions, show original amount; otherwise show current amount
                        const displayAmount = row.original.type === 'Provision' && row.original.originalAmount !== undefined
                            ? row.original.originalAmount
                            : cell.getValue<number>();
                        return <Typography fontSize={isMobile ? 14 : 16} sx={{ color }}>{formatAmount?.(displayAmount)}</Typography>;
                    },
                },
                {
                    accessorKey: 'dateLin',
                    header: 'Date Linked',
                    size: 120,
                    Cell: ({ row }) => {
                        const dateLinked = row.original?.dateLinked;
                        const color = row.original.type === 'Provision' ? 'var(--color-provision-text)' : 'inherit';
                        return <Typography fontSize={isMobile ? 14 : 16} sx={{ color }}>{dateLinked ? formatDate(new Date(dateLinked)) : '-'}</Typography>;
                    },
                },
                {
                    accessorKey: 'remainingBalance',
                    header: 'Remaining Balance',
                    size: 120,
                    enableHiding: true,
                    columnDefType: 'display',
                    Cell: ({ cell, row }: any) => {
                        const color = row.original.type === 'Provision' ? 'var(--color-provision-text)' : 'inherit';
                        const value = (cell as any).getValue?.() as number;
                        return <Typography fontSize={isMobile ? 14 : 16} sx={{ color }}>{formatAmount?.(value)}</Typography>;
                    },
                },
                {
                    accessorKey: 'description',
                    header: 'Description',
                    size: 180,
                    Cell: ({ cell, row }) => {
                        const color = row.original.type === 'Provision' ? 'var(--color-provision-text)' : 'inherit';
                        return <Typography fontSize={isMobile ? 14 : 16} sx={{ color }}>{cell.getValue<string>()}</Typography>;
                    },
                },
                {
                    accessorKey: 'category',
                    header: 'Category',
                    size: 120,
                    enableHiding: true,
                    columnDefType: 'display',
                    Cell: ({ cell, row }: any) => {
                        const color = row.original.type === 'Provision' ? 'var(--color-provision-text)' : 'inherit';
                        const value = (cell as any).getValue?.() as string;
                        return <Typography fontSize={isMobile ? 14 : 16} sx={{ color }}>{value || '-'}</Typography>;
                    },
                },
                {
                    id: 'unlink',
                    header: 'Action',
                    size: 60,
                    Cell: ({ row }) => {
                        const shouldShowUnlink = data?.transaction?.type === 'real' || row.original.linkedTransactionId;
                        if (!shouldShowUnlink || row.original.guid === transactionId || !canChangeLinks) return null;
                        return (
                            <Tooltip
                                title="Unlink" placement="bottom" arrow sx={{ zIndex: theme => theme.zIndex.tooltip }}
                            >
                                <Button
                                    size="small"
                                    sx={{ minWidth: 'auto', padding: 0 }}
                                    onClick={(e) => {
                                        // Without this, the click bubbles up to the row's own
                                        // onClick (muiTableBodyRowProps below), which opens a
                                        // nested TransactionDetail for this row on top of the
                                        // Unlink confirmation that's about to open too.
                                        e.stopPropagation();
                                        setUnlinkingTransactionId(row.original.guid);
                                        setOpenModal('unlink');
                                    }}
                                >
                                    <svg width="24px" height="24px" viewBox="0 0 17 17" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink">
                                        <path d="M2.134 5.139l1.402-2.587c1.086-1.648 3.262-2.095 4.871-1.029l-0.551 0.833c-1.149-0.759-2.703-0.44-3.464 0.709l-1.402 2.587c-0.781 1.188-0.464 2.742 0.687 3.501 1.143 0.752 2.41 0.547 3.313-0.538l0.768 0.641c-0.742 0.892-1.694 1.352-2.678 1.352-0.655 0-1.323-0.204-1.954-0.62-1.61-1.064-2.056-3.239-0.992-4.849zM4.041 8.034l-0.13 0.129 0.705 0.709 0.131-0.13c0.975-0.975 2.561-0.975 3.535 0s0.975 2.561 0 3.535l-3.023 3.024c-0.975 0.975-2.561 0.975-3.535 0s-0.975-2.561 0-3.535l1.058-1.059-0.707-0.707-1.058 1.060c-1.364 1.364-1.364 3.585 0 4.949 0.683 0.683 1.578 1.023 2.475 1.023s1.792-0.341 2.475-1.023l3.023-3.024c1.364-1.364 1.364-3.585 0-4.949-1.367-1.367-3.587-1.364-4.949-0.002zM10.963 7h4.074v-1h-4.074v1zM10.495 8.347l1.951 1.127 0.5-0.865-1.951-1.127-0.5 0.865zM10.75 1.156l-1.58 2.737 0.865 0.5 1.58-2.736-0.865-0.501zM15.867 2.725l-0.5-0.865-4.992 2.882 0.5 0.865 4.992-2.882z" fill="#ff0000" />
                                    </svg>
                                </Button>
                            </Tooltip>
                        );
                    },
                },
            ];

            // Category is never useful in the history tab; Date Linked only matters
            // when viewing a provision (it can have several linked real transactions).
            return columns.filter(col => {
                if (col.accessorKey === 'category') return false;
                if (col.accessorKey === 'dateLin' && !isViewingProvision) return false;
                return true;
            });
        },
        [formatDate, formatAmount, isMobile, transactionId, data, canChangeLinks]
    );

    const reanalyzeWithDescriptionOnly = (sourceTrxGuid: string, start?: number, length?: number) => {
        let { url, method } = buildApiRequestConfig(routes.provisionTemplate.analyzePatternByDescription, [{ param: 'transactionGuid', value: sourceTrxGuid }]);
        const qp: Record<string, any> = {};
        if (start !== undefined) qp.start = start;
        if (length !== undefined) qp.length = length;
        if (Object.keys(qp).length > 0) {
            const query = new URLSearchParams(qp).toString();
            url = `${url}?${query}`;
        }
        setAnalyzePattern({ url, method });
    };

    React.useEffect(() => {
        if (!open) return;
        
        // If invoiceNumber is provided, use it; otherwise use transactionId
        const fetchId = invoiceNumber || transactionId;
        if (!fetchId) return;
        
        let params = [{ param: 'guid', value: transactionId }];
        if (invoiceNumber) {
            // invoiceNumber alone isn't unique across entities (Xero invoice
            // numbering restarts small per tenant) - without entityId here the
            // lookup can silently return a different entity's transaction.
            params.push({ param: 'invoiceNumber', value: invoiceNumber });
            params.push({ param: 'entityId', value: user?.entityId });
        }
        let { url, method } = buildApiRequestConfig(routes.transaction.fetchByGuid, params);

        setFetch({ url, method });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [transactionId, invoiceNumber, open, user?.entityId]);

    React.useEffect(() => {
        if (tabValue === 1 && transactionId && open) {
            const { url, method } = buildApiRequestConfig(routes.transaction.fetchRelated, [{ param: 'guid', value: transactionId }]);
            setFetchRelated({ url, method });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabValue, transactionId, open, data?.transaction?.type]);

    // Pattern Analysis tab (recurring provisions only) - same endpoint/shape
    // related-transactions-detail.tsx already uses from the Provisions page,
    // keyed by the template rather than this specific occurrence's guid.
    React.useEffect(() => {
        const provisionTemplateId = data?.transaction?.provisionTemplateId;
        if (tabValue === 2 && provisionTemplateId && open) {
            const { url, method } = buildApiRequestConfig(routes.provisionTemplate.fetchTransactionsById, [{ param: 'id', value: provisionTemplateId }]);
            setFetchPatternAnalysis({ url, method });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabValue, data?.transaction?.provisionTemplateId, open]);

    // Whether this entity even has Xero connected - gates both Xero tabs
    // below (and their own candidate fetches), same status check
    // XeroSyncButton uses. Fetched once per open, not per tab - cheap single
    // GET, and needed just to decide whether the tabs show up at all.
    React.useEffect(() => {
        if (!open || !user?.entityId) return;
        const { url, method } = buildApiRequestConfig(routes.xero.getIntegration, [{ param: 'entityId', value: user.entityId }]);
        setFetchXeroIntegration({ url, method });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, user?.entityId]);

    // Xero candidates tab (real transactions only) - a provision somewhere
    // else may have an unmatched Xero payment dated the same day as this
    // transaction (see sourceData.xeroPayments, xero-import-service.js /
    // getXeroPaymentCandidates, transaction-controller.js). Its own dedicated
    // endpoint, fetched only once this tab is actually opened - not worth a
    // round trip on every transaction detail open when it's rarely used.
    React.useEffect(() => {
        // Resolved guid, not the transactionId prop - this modal can also be
        // opened via invoiceNumber alone (transactionId null, e.g. from the
        // Invoices page's linked-status icon), in which case the guid only
        // becomes known once data.transaction itself has loaded.
        const guid = data?.transaction?.guid;
        if (tabValue === 3 && data?.transaction?.type === 'real' && guid && open && isXeroConnected) {
            setXeroCandidateSelection({});
            const { url, method } = buildApiRequestConfig(routes.transaction.fetchXeroPaymentCandidates, [{ param: 'guid', value: guid }]);
            setFetchXeroCandidates({ url, method });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabValue, data?.transaction?.type, data?.transaction?.guid, open, isXeroConnected]);

    // Inverse of the above - the Xero Payment tab (provisions with an
    // unmatched sourceData.xeroPayments) also checks for unlinked REAL
    // transactions dated the same day as one of those payments, so the same
    // linking suggestion works from either side.
    React.useEffect(() => {
        const guid = data?.transaction?.guid;
        // Once this provision is already linked to something, the candidate
        // search is moot - show what it's actually linked to instead (see
        // the Xero Payment tab's render below), not a stale "here's what you
        // could link" list.
        if (tabValue === 3 && data?.transaction?.type === 'provision' && guid && open && isUnlinked && isXeroConnected) {
            setRealCandidateSelection({});
            const { url, method } = buildApiRequestConfig(routes.transaction.fetchRealCandidatesForPayment, [{ param: 'guid', value: guid }]);
            setFetchRealCandidates({ url, method });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabValue, data?.transaction?.type, data?.transaction?.guid, open, isUnlinked, isXeroConnected]);

    // Pre-tick any candidate whose own amount already equals this
    // transaction's - a single invoice/payment that fully accounts for it is
    // the common case, so there's rarely a reason to make the user tick it
    // manually. Runs once the data actually arrives (not alongside the
    // fetch-triggering effects above, which only know the request is
    // starting, not what it'll come back with).
    React.useEffect(() => {
        const candidates = fetchXeroCandidates.data?.data;
        if (!candidates) return;
        const transactionAmount = Math.abs(Number(data?.transaction?.amount) || 0);
        const autoSelected: Record<string, boolean> = {};
        candidates.forEach((c: any) => {
            // matchedPayment.amount, not the invoice's own amount - see the
            // selectedTotal comment below for why they can differ.
            if (Math.abs(Math.abs(Number(c.matchedPayment?.amount ?? c.amount) || 0) - transactionAmount) < 0.005) autoSelected[c.guid] = true;
        });
        setXeroCandidateSelection(autoSelected);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchXeroCandidates.data]);

    // Same idea, inverse direction - pre-tick a candidate real transaction
    // whose amount already equals this invoice/provision's own.
    React.useEffect(() => {
        const candidates = fetchRealCandidates.data?.data;
        if (!candidates) return;
        const provisionAmount = Math.abs(Number(data?.transaction?.amount) || 0);
        const autoSelected: Record<string, boolean> = {};
        candidates.forEach((c: any) => {
            if (Math.abs(Math.abs(Number(c.amount) || 0) - provisionAmount) < 0.005) autoSelected[c.guid] = true;
        });
        setRealCandidateSelection(autoSelected);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchRealCandidates.data]);

    // Resolves a Find Matching row's invoiceNumber to the Invoice record's
    // own id (InvoicePreview needs the PK, not the invoiceNumber) - same
    // lookup invoices.tsx uses for its own invoiceNumber deep-link.
    React.useEffect(() => {
        if (!previewInvoiceNumber || !user?.entityId) return;
        const { url, method } = buildApiRequestConfig(routes.invoices.fetchByEntity, [
            { param: 'entityId', value: user.entityId },
            { param: 'invoiceNumber', value: previewInvoiceNumber },
            { param: 'page', value: 1 },
            { param: 'pageSize', value: 5 },
        ]);
        setFetchInvoiceByNumber({ url, method });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [previewInvoiceNumber, user?.entityId]);

    React.useEffect(() => {
        const { data: lookupData, isLoading, error } = fetchInvoiceByNumber;
        if (!previewInvoiceNumber || isLoading || (!lookupData && !error)) return;
        // invoiceNumber is a LIKE match server-side, so filter for the exact match
        const match = (lookupData?.invoices || []).find((inv: any) => inv.invoiceNumber === previewInvoiceNumber);
        if (match) {
            setPreviewInvoiceId(parseInt(match.id));
        } else {
            showError(`Invoice ${previewInvoiceNumber} not found.`);
            setPreviewInvoiceNumber(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchInvoiceByNumber]);

    React.useEffect(() => {
        if (autoOpenLinkModal && openModal !== 'link') {
            setOpenModal('link');
            // Consume the trigger immediately instead of waiting for the Link
            // popup's own close button to reset it. Otherwise, if the user
            // dismisses the auto-opened Link popup any other way (e.g. it
            // auto-closes after a successful link), the parent's flag stays
            // stuck true - so the next unrelated openModal change back to ''
            // (e.g. confirming an Unlink) re-triggers this effect and pops
            // the Link modal back open on top of it.
            onAutoOpenLinkModalClose?.();
        }
    }, [autoOpenLinkModal, open, openModal, onAutoOpenLinkModalClose]);

    React.useEffect(() => {
        const { data, isLoading, error } = link;
        if (data && !isLoading) {
            showSuccess("Transactions linked successfully");
            setOpenModal('');
            onLink?.();
            // Reset link state to avoid re-triggering, then refetch
            setLink(null);
            if (transactionId && open) {
                let params = [{ param: 'guid', value: transactionId }];
                if (invoiceNumber) {
                    params.push({ param: 'invoiceNumber', value: invoiceNumber });
                }
                const { url, method } = buildApiRequestConfig(routes.transaction.fetchByGuid, params);
                setFetch({ url, method });
            }
        } 
        if (error) {
            showError(error ? error.toString() : "Failed to link transactions");
            setLink(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [link.data, link.isLoading, link.error]);

    React.useEffect(() => {
        const { data, isLoading, error } = unlink;
        if (data && !isLoading) {
            showSuccess("Transactions unlinked successfully");
            setOpenModal('');
            onLink?.();
            // Reset unlink state to avoid re-triggering, then refetch
            setUnlink(null);
            if (transactionId && open) {
                let params = [{ param: 'guid', value: transactionId }];
                if (invoiceNumber) {
                    params.push({ param: 'invoiceNumber', value: invoiceNumber });
                }
                const { url, method } = buildApiRequestConfig(routes.transaction.fetchByGuid, params);
                setFetch({ url, method });
            }
        } 
        if (error) {
            showError(error ? error.toString() : "Failed to unlink transactions");
            setUnlink(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unlink.data, unlink.isLoading, unlink.error]);

    React.useEffect(() => {
        const { data, isLoading, error } = createProvision;
        // useApi's setCreateProvision(null) only clears the request config, never
        // the previous response - so `data` here stays truthy indefinitely after a
        // successful call, and this effect re-evaluates on every later render this
        // hook instance is involved in. Guard against reprocessing the same
        // already-handled response (e.g. re-opening the review modal long after
        // the original creation actually finished).
        if (data && !isLoading && data?.data?.id && processedCreateProvisionRef.current !== data.data.id) {
            processedCreateProvisionRef.current = data.data.id;
            console.log('[ProvisionThis] createProvision succeeded, resetting isDescriptionOnlyMatch/descriptionOptions. Response:', data);
            showSuccess("Provision created successfully");
            setOpenModal('');
            setDescriptionOptions(null);
            setIsDescriptionOnlyMatch(false);
            onLink?.();

            // Show what the backend actually matched + marked (transactionsToMark,
            // returned straight from createProvisionTemplate) so the user can review it -
            // this is exactly what got linked, not a separately-computed snapshot.
            if (Array.isArray(data.transactionsToMark) && data.transactionsToMark.length > 0) {
                console.log('[ProvisionThis] showing review modal for transactionsToMark:', data.transactionsToMark);
                setCreatedProvisionIdForMark(data.data.id);
                setProvisionTransactionsToConfirm(data.transactionsToMark);
                setShowProvisionTransactions(true);
            }

            // Reset createProvision state to avoid re-triggering, then refetch so the
            // "Provision This" button reflects that this transaction is now provisioned
            setCreateProvision(null);
            if (transactionId && open) {
                let params = [{ param: 'guid', value: transactionId }];
                if (invoiceNumber) {
                    params.push({ param: 'invoiceNumber', value: invoiceNumber });
                }
                const { url, method } = buildApiRequestConfig(routes.transaction.fetchByGuid, params);
                setFetch({ url, method });
            }
        }
        if (error) {
            showError(error ? error.toString() : "Failed to create provision");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createProvision.data, createProvision.isLoading, createProvision.error]);

    React.useEffect(() => {
        const { data, isLoading, error } = markTrx;
        // Same useApi quirk as createProvision above.
        if (data && !isLoading && processedMarkTrxRef.current !== data) {
            processedMarkTrxRef.current = data;
            showSuccess("Transactions marked as provisioned successfully");
            setShowProvisionTransactions(false);
            setCreatedProvisionIdForMark(null);
            setProvisionTransactionsToConfirm([]);
            setMarkTrx(null);
        }
        if (error) {
            showError(error ? error.toString() : "Failed to mark transactions");
            setMarkTrx(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [markTrx.data, markTrx.isLoading, markTrx.error]);

    React.useEffect(() => {
        if (fetch.error && !fetch.isLoading && onNotFound) onNotFound();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetch.error, fetch.isLoading]);

    React.useEffect(() => {
        const { data, isLoading } = fetch;
        if (data && !isLoading) {
            const linkedTrx = (data.linkedTransactions || []).map((trx: any) => 
                ({
                    ...trx,
                    type: trx.type === 'provision' ? 'Provision' : 'Import',
                })
            );
            data.linkedTransactions = linkedTrx;

            setData(data);
            setIsLinkable(data.transaction?.isLinkable ?? false);
        }
    }, [fetch]);

    React.useEffect(() => {
        const { data, isLoading } = fetchRelated;
        if (data && !isLoading) {
            const relatedTrx = (data.relatedTransactions || [])
                .map((trx: any) =>
                    ({
                        ...trx,
                        type: trx.type === 'provision' ? 'Provision' : 'Import',
                        bank: trx.bankAccount?.accountName || '-',
                        category: trx.category?.name || '-',
                    })
                )
                .sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());
            setRelatedTransactions(relatedTrx);
        }
    }, [fetchRelated]);

    React.useEffect(() => {
        const { data, isLoading, error } = analyzePattern;
        if (data && !isLoading) {
            // setAnalyzePattern(null) below only clears the request config, not this
            // response — analyzePattern.data stays the same object afterwards. Since
            // this effect also re-runs whenever pendingDescriptionGuid changes (it's
            // both read and written here), bail out if we've already handled this
            // exact response, otherwise it reprocesses stale data forever.
            if (processedAnalysisRef.current === data) return;
            processedAnalysisRef.current = data;

            // Check if there are matches
            if (!data.hasMatches) {
                if (!pendingDescriptionGuid) {
                    // first pass – prompt user for description char settings
                    setPendingDescriptionGuid(data.sourceTransaction?.guid || null);
                    setPendingDescriptionText(data.sourceTransaction?.description || '');
                    setShowMatchSettings(true);
                } else {
                    // result of description-only analysis: show confirmation
                    setPatternAnalysisMessage(data.analysis?.message || "Unable to analyze pattern. Please fill in the details manually.");
                    setShowPatternConfirmation(true);
                    // Set default data for if they confirm
                    const sourceTransaction = data.sourceTransaction || {};
                    setProvisionThisData({
                        date: adjustStartDate(sourceTransaction.date, data.analysis),
                        amount: sourceTransaction.amount,
                        description: sourceTransaction.description
                    });
                    if (data.analysis) {
                        setIntervalAnalysis(data.analysis);
                    }
                    setPendingDescriptionGuid(null);
                }
            } else {
                // Matches found - proceed with provision creation
                const sourceTransaction = data.sourceTransaction || {};
                setProvisionThisData({
                    date: adjustStartDate(data.analysis?.startDate || sourceTransaction.date, data.analysis),
                    amount: sourceTransaction.amount,
                    description: sourceTransaction.description
                });
                // Store analysis data for pre-filling recurrence settings
                if (data.analysis) {
                    setIntervalAnalysis(data.analysis);
                }
                setPendingDescriptionGuid(null);
                setOpenModal('add-provision');
            }
            // Reset analyzePattern state
            setAnalyzePattern(null);
        } else if (error && !isLoading) {
            if (processedAnalysisRef.current === error) return;
            processedAnalysisRef.current = error;

            // API failed - use fallback data and open modal
            showError("Failed to analyze pattern. Using transaction data instead.");
            setIntervalAnalysis(null);
            setOpenModal('add-provision');
            // Reset analyzePattern state
            setAnalyzePattern(null);
        }
    }, [analyzePattern, showError, setAnalyzePattern, pendingDescriptionGuid]);

    const handleLinkTransactions = (transactionIds: string[]) => {
        const { url, method } = buildApiRequestConfig(routes.transaction.link);
        setLink({
            url,
            method,
            body: {
                linkedTransactionId: transactionId,
                transactionIds,
            },
        });
    };

    const handleCreateProvisionFromTransaction = (newProvision: any) => {
        const { url, method } = buildApiRequestConfig(routes.provisionTemplate.create);
        const body: any = {
            ...newProvision,
            entityId: data?.transaction?.entityId,
            appliedTaxBreakdown: JSON.stringify(newProvision.appliedTaxBreakdown),
            isDescriptionOnlyMatch,
            provisionThisSourceGuid: transactionId
        };
        if (descriptionOptions) {
            body.descriptionOptions = descriptionOptions;
        }
        console.log('[ProvisionThis] creating provision, request body:', JSON.parse(JSON.stringify(body)));
        setCreateProvision({
            url,
            method,
            body
        });
    };

    const handleUnlinkTransaction = (transactionId: string) => {
        if (!data?.transaction?.guid) return;
        const { url, method } = buildApiRequestConfig(routes.transaction.unlink, [{ param: 'guid', value: data?.transaction?.guid }]);
        setUnlink({
            url,
            method,
            body: {
                targetTransactionGuid: transactionId
            }
        });
    };

    // The unlink endpoint only accepts one targetTransactionGuid per call, and
    // useApi's setUnlink can't have more than one request in flight at a time
    // (calling it repeatedly in the same tick just overwrites the pending
    // config) - so unticking several already-linked rows in the Find Matching
    // Transactions modal at once (see its onConfirm below) needs its own
    // sequential loop instead of reusing that hook.
    const handleUnlinkTransactions = async (transactionIds: string[]) => {
        if (!data?.transaction?.guid || transactionIds.length === 0) return;
        const { url, method } = buildApiRequestConfig(routes.transaction.unlink, [{ param: 'guid', value: data.transaction.guid }]);
        const token = Cookies.get('token');
        let failures = 0;
        for (const targetTransactionGuid of transactionIds) {
            try {
                await axios({
                    method,
                    url,
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    data: { targetTransactionGuid },
                });
            } catch {
                failures++;
            }
        }
        if (failures > 0) {
            showError(`Failed to unlink ${failures} of ${transactionIds.length} transaction(s).`);
        } else {
            showSuccess("Transactions unlinked successfully");
        }
        onLink?.();
        setOpenModal('');
        if (transactionId && open) {
            let params = [{ param: 'guid', value: transactionId }];
            if (invoiceNumber) {
                params.push({ param: 'invoiceNumber', value: invoiceNumber });
            }
            const { url: fetchUrl, method: fetchMethod } = buildApiRequestConfig(routes.transaction.fetchByGuid, params);
            setFetch({ url: fetchUrl, method: fetchMethod });
        }
    };

    const onToggleStatus = () => {
        if (!transactionId || !canChangeLinks) return;
        const { url, method } = buildApiRequestConfig(routes.transaction.toggleLinkable, [{ param: 'guid', value: transactionId }]);
        setToggleLinkable({
            url,
            method,
        });
        setIsLinkable(!isLinkable);
    };

    // The optimistic setIsLinkable above only updates this modal's own local
    // state - without also notifying the parent (same onLink callback the
    // link/unlink flows already use to trigger a refetch), the Transactions
    // page's row data keeps the stale isLinkable value after the modal
    // closes, so the action menu's Link option stays wrongly enabled/disabled
    // until a full page reload re-fetches fresh data.
    React.useEffect(() => {
        const { data, isLoading, error } = toggleLinkable;
        if (data && !isLoading) {
            onLink?.();
            setToggleLinkable(null);
        }
        if (error) {
            showError(error ? error.toString() : "Failed to update link status");
            setIsLinkable(prev => !prev);
            setToggleLinkable(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [toggleLinkable.data, toggleLinkable.isLoading, toggleLinkable.error]);

    const isRealTransaction = data?.transaction?.type === 'real';
    const isProvisionRelated = !!data?.transaction?.provisionThisTemplateId;

    let linkTooltipTitle = '';
    let linkButtonDisabled = !isLinkable || fetch.isLoading || !canChangeLinks;

    if (isRealTransaction) {
        if (!isLinkable) {
            linkTooltipTitle = 'You cannot link any more provisions, as this transaction is already closed.';
        } else if (hasLinkedTransactions) {
            linkTooltipTitle = 'This transaction is already linked.';
            linkButtonDisabled = true;
        }
    } else if (hasLinkedTransactions) {
        linkTooltipTitle = 'This transaction is already linked';
    }
    if (!canChangeLinks) linkTooltipTitle = "You don't have permission to do this";

    // Shown to everyone, disabled without the "Add Provision" permission
    // (Create/Update/Delete buttons stay visible but disabled).
    const canProvisionThis = can('transactions.addProvision', 'create') || can('provisions', 'create');
    const provisionThisDisabled = hasLinkedTransactions || isProvisionRelated || !canProvisionThis;
    const provisionThisTooltipTitle = !canProvisionThis
        ? "You don't have permission to do this"
        : hasLinkedTransactions
            ? 'This transaction is linked to a provision.'
            : isProvisionRelated
                ? 'This transaction is related to a provision.'
                : '';

    return (
        <Dialog open={open} onClose={() => {onClose(); setTabValue(0);}} maxWidth={isMobile ? "sm" : "md"} fullWidth>
            <Dialog
                open={analyzePattern.isLoading}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        textAlign: 'center',
                        padding: 3,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                    }
                }}
            >
                <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                    <CircularProgress sx={{ color: 'var(--color-primary)' }} size={50} />
                    <Typography variant="h6" fontWeight={600}>
                        Analyzing Transaction Pattern
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Detecting recurrence patterns and historical matches...
                    </Typography>
                </Box>
            </Dialog>
            <AddProvisionPopup 
                open={openModal === 'add-provision'}
                onClose={() => {
                    console.log('[ProvisionThis] AddProvisionPopup cancelled, resetting isDescriptionOnlyMatch/descriptionOptions');
                    setOpenModal('detail');
                    setProvisionThisData(null);
                    setIntervalAnalysis(null);
                    setIsDescriptionOnlyMatch(false);
                    setDescriptionOptions(null);
                }}
                initialStartDate={provisionThisData?.date || data?.transaction?.date}
                initialAmount={provisionThisData?.amount ? String(provisionThisData.amount) : String(data?.transaction?.amount || '')}
                initialDescription={provisionThisData?.description || data?.transaction?.description}
                intervalAnalysis={intervalAnalysis}
                onCreate={handleCreateProvisionFromTransaction}
            />
            <ProvisionTransactionsDisplay
                open={showProvisionTransactions}
                provisionId={createdProvisionIdForMark}
                transactions={provisionTransactionsToConfirm}
                isLoading={markTrx.isLoading}
                onConfirm={(trxGuids: string[]) => {
                    if (!createdProvisionIdForMark) return;
                    const { url, method } = buildApiRequestConfig(routes.provisionTemplate.markTransactionAsProvisioned, [{ param: 'id', value: createdProvisionIdForMark }]);
                    setMarkTrx({ url, method, body: { transactionGuids: trxGuids } });
                }}
                onClose={() => {
                    setShowProvisionTransactions(false);
                    setCreatedProvisionIdForMark(null);
                    setProvisionTransactionsToConfirm([]);
                }}
            />
            <DeletePopup
                open={showPatternConfirmation}
                message="Continue with Provision?"
                description={patternAnalysisMessage}
                onCancel={() => {
                    setShowPatternConfirmation(false);
                    setPatternAnalysisMessage('');
                    setProvisionThisData(null);
                    setIntervalAnalysis(null);
                }}
                onConfirm={() => {
                    setShowPatternConfirmation(false);
                    setOpenModal('add-provision');
                }}
            />

            <DescriptionMatchSettings
                open={showMatchSettings}
                sourceDescription={pendingDescriptionText}
                onClose={() => {
                    setShowMatchSettings(false);
                    setPendingDescriptionGuid(null);
                }}
                onConfirm={(start, length) => {
                    console.log('[ProvisionThis] description-only match settings confirmed, setting isDescriptionOnlyMatch=true', { start, length, pendingDescriptionGuid });
                    setShowMatchSettings(false);
                    setDescriptionOptions({ start, length });
                    setIsDescriptionOnlyMatch(true);
                    if (pendingDescriptionGuid) {
                        reanalyzeWithDescriptionOnly(pendingDescriptionGuid, start, length);
                    }
                }}
            />
            <DeletePopup
                open={openModal === 'unlink'}
                message="Unlink this Transaction?"
                description="The transaction will remain in your records but will no longer be linked to this provision."
                onCancel={() => setOpenModal('')}
                image={{ src: trxImage, width: 150, height: 150 }}
                onConfirm={() => {
                    if (!unlinkingTransactionId) return;
                    handleUnlinkTransaction(unlinkingTransactionId);
                    setOpenModal('');
                }}
            />
            <LinkTransactionPopup
                open={openModal === 'link'}
                onClose={() => {
                    setOpenModal('detail');
                    onAutoOpenLinkModalClose?.();
                }}
                onConfirm={(selectedTransactions, transactionsToUnlink, shouldCloseTransaction) => {
                    if (selectedTransactions.length > 0) {
                        handleLinkTransactions(selectedTransactions);
                    }
                    if (transactionsToUnlink.length > 0) {
                        handleUnlinkTransactions(transactionsToUnlink);
                    }
                    // The provisions just linked fully cover this transaction's
                    // amount - close it the same way the manual OPEN/CLOSED
                    // toggle does (onToggleStatus reads the transaction's
                    // current isLinkable, which is true/open here since the
                    // Link action wouldn't have been reachable otherwise).
                    if (shouldCloseTransaction) {
                        onToggleStatus();
                    }
                }}
                provisionAmount={data?.transaction?.amount || 0}
                isProvision={data?.transaction?.type === 'provision'}
                initialToleranceRange={{ min: companyStatus.lowestAmount || -9999, max: companyStatus.highestAmount || 9999 }}
                maxToleranceAmount={companyStatus.highestAmount ? Math.abs(companyStatus.highestAmount) : 10000}
                transactionBankId={data?.transaction?.bankId}
                sourceDescription={data?.transaction?.description}
                sourceDate={data?.transaction?.date}
                sourceOriginalAmount={data?.transaction?.originalAmount}
                alreadyLinkedTransactions={otherLinkedTransactions}
                isSubmitting={link.isLoading}
                onRowClick={(guid, invoiceNumber) => {
                    if (invoiceNumber) {
                        setPreviewInvoiceNumber(invoiceNumber);
                    } else {
                        setSelectedLinkedTransactionId(guid);
                    }
                }}
            />
            {previewInvoiceId && (
                <InvoicePreview
                    open={!!previewInvoiceId}
                    invoiceId={previewInvoiceId}
                    onClose={() => {
                        setPreviewInvoiceId(null);
                        setPreviewInvoiceNumber(null);
                    }}
                />
            )}
            <TransactionDetail
                open={!!selectedLinkedTransactionId}
                transactionId={selectedLinkedTransactionId}
                onClose={() => setSelectedLinkedTransactionId(null)}
                companyStatus={companyStatus}
                onLink={onLink}
            />
            <DialogContent sx={{ position: 'relative', pt: 3, px: isMobile ? 2 : 3, pb: 0 }}>
                <Box sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    background: (data?.transaction?.type === 'provision' && !isPaidAtImportProvision) ? 'var(--color-provision-text)' : 'var(--color-primary)',
                    py: 0.75,
                    pl: 5,
                    pr: 3,
                    color: '#fff',
                    fontSize: 14,
                    fontWeight: 600,
                    clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 15% 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    zIndex: 1,
                }}>
                    { (data?.transaction?.type === 'provision' && data?.transaction?.invoiceNumber) ? (
                        <Box display="flex" justifyContent="flex-end">
                            <Typography fontSize={14} fontStyle="italic">
                                Generated from INVOICE #: <span style={{ textDecoration: 'underline', cursor: 'pointer', fontWeight: 600 }} onClick={() => navigate(`/invoices?invoiceNumber=${data?.transaction?.invoiceNumber}`)}>{data?.transaction?.invoiceNumber}</span>
                            </Typography>
                        </Box>
                    ) : <>
                        <Database size={18} color="#fff"/> {(data?.transaction?.type === 'provision' && !isPaidAtImportProvision) ? 'Provision' : 'Imported'} Data
                    </>}
                </Box>
                {fetch.isLoading ? (
                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={isMobile ? 150 : 200} gap={2} width="100%">
                        <CircularProgress size={40} />
                        <Typography variant="h6" color="text.secondary">
                            Loading transaction details...
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <Box 
                            display="flex" 
                            width="100%"
                        >
                            <Box width={200}>
                                <Typography fontSize={isMobile ? 12 : 16} fontWeight={600} color="primary">
                                    {(data?.transaction?.type === 'provision' && !isPaidAtImportProvision) ? 'PROVISION' : 'TRANSACTION'}
                                </Typography>
                            </Box>
                        </Box>
                        <Box 
                            display="flex" 
                            width="100%" 
                            flexDirection={isMobile ? "column" : "row"} 
                            sx={{ 
                                px: 0.5,
                                py: 0.75,
                            }}>
                            <Box width={150}>
                                <Typography fontSize={isMobile ? 12 : 16} color="var(--color-text-muted)">
                                    Date
                                </Typography>
                            </Box>
                            <Box width={180}>
                                <Typography fontSize={isMobile ? 12 : 16} color="var(--color-text-muted)">
                                    Amount
                                </Typography>
                            </Box>
                            <Box width={400}>
                                <Typography fontSize={isMobile ? 12 : 16} color="var(--color-text-muted)">
                                    Description
                                </Typography>
                            </Box>
                            <Box width={150}>
                                <Typography fontSize={isMobile ? 12 : 16} color="var(--color-text-muted)">
                                    Bank
                                </Typography>
                            </Box>
                            <Box width={150}>
                                <Typography fontSize={isMobile ? 12 : 16} color="var(--color-text-muted)">
                                    Category
                                </Typography>
                            </Box>
                        </Box>
                        <Box 
                            display="flex" 
                            width="100%" 
                            flexDirection={isMobile ? "column" : "row"} 
                            sx={{ 
                                border: '1px solid #d7d7d7',
                                px: 0.5,
                                py: 0.75,
                                borderRadius: 1,
                                background: 'var(--color-container-background)',
                                color: (data?.transaction?.type === 'provision' && !isPaidAtImportProvision) ? 'var(--color-provision-text)' : 'inherit',
                            }}>
                            <Box width={150}>
                                <Tooltip
                                    title={data?.transaction?.dateLinked ? `Date Linked: ${formatDate(new Date(data.transaction.dateLinked))}` : ''}
                                    disableHoverListener={!data?.transaction?.dateLinked}
                                >
                                    <Typography fontSize={isMobile ? 14 : 16} fontWeight={500} sx={{ width: 'fit-content' }}>
                                        {formatDate(data?.transaction?.date || new Date())}
                                    </Typography>
                                </Tooltip>
                            </Box>
                            <Box width={180}>
                                <Typography fontSize={isMobile ? 14 : 16} fontWeight={500}>
                                    {formatAmount?.(data?.transaction?.amount)}
                                </Typography>
                            </Box>
                            <Box width={400}>
                                <Typography fontSize={isMobile ? 14 : 16} fontWeight={500}>
                                    {data?.transaction?.description}
                                </Typography>
                            </Box>
                            <Box width={150}>
                                <Typography fontSize={isMobile ? 14 : 16} fontWeight={500}>
                                    {data?.transaction?.bank || '-'}
                                </Typography>
                            </Box>
                            <Box width={150}>
                                <Typography fontSize={isMobile ? 14 : 16} fontWeight={500}>
                                    {data?.transaction?.category || '-'}
                                </Typography>
                            </Box>
                        </Box>
                        {(data?.transaction?.sourceData?.xeroDescription || data?.transaction?.sourceData?.overriddenByXero) && (
                            <Box sx={{ px: 0.5, pt: 0.5 }}>
                                <Typography fontSize={isMobile ? 10 : 11} fontStyle="italic" color="var(--color-text-muted)">
                                    {data.transaction.sourceData.xeroDescription && (
                                        <>Local description: {data.transaction.originalDescription || '-'}</>
                                    )}
                                    {data.transaction.sourceData.xeroDescription && data.transaction.sourceData.overriddenByXero && ' — '}
                                    {data.transaction.sourceData.overriddenByXero && (
                                        <>Originally: {data.transaction.sourceData.overriddenByXero.categoryName || '-'}
                                        {' '}(before Xero sync{data.transaction.sourceData.overriddenByXero.overriddenAt ? ` on ${formatDate(new Date(data.transaction.sourceData.overriddenByXero.overriddenAt))}` : ''})</>
                                    )}
                                </Typography>
                            </Box>
                        )}
                        {isUnlinked && data?.transaction?.sourceData?.xeroPayments?.length > 0 && (
                            <Box sx={{ px: 0.5, pt: 0.5 }}>
                                <Typography fontSize={isMobile ? 10 : 11} fontStyle="italic" color="var(--color-text-muted)">
                                    Xero recorded a payment FineAnts couldn't auto-link — see the "Xero Payment" tab below.
                                </Typography>
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>
            <Divider sx={{ my: 1 }}/>
            {!fetch.isLoading && 
                <DialogContent sx={{ py: 0 }}>
                    <Tabs 
                        value={tabValue} 
                        onChange={(event, newValue) => setTabValue(newValue)}
                        sx={{ borderBottom: 1, borderColor: 'divider', mb: 1, minHeight: 'auto', '& .MuiTab-root': { minHeight: 32, py: 0.5, px: 1.5, fontSize: '14px' } }}
                    >
                        {/* Explicit value on every Tab, not just the conditional ones -
                        MUI's default index-from-position assignment isn't
                        guaranteed to skip a sibling that renders false, and
                        two conditional tabs sitting next to each other here
                        makes that ambiguity worth just not depending on. */}
                        <Tab label="History" value={0}/>
                        <Tab label="Related" value={1} />
                        {data?.transaction?.type === 'provision' && !!data?.transaction?.provisionTemplateId && (
                            <Tab label="Pattern Analysis" value={2} />
                        )}
                        {isXeroConnected && data?.transaction?.sourceData?.xeroPayments?.length > 0 && (
                            <Tab label="Xero Payment" value={3} />
                        )}
                        {isXeroConnected && data?.transaction?.type === 'real' && (
                            <Tab label="Xero" value={3} />
                        )}
                    </Tabs>

                    {/* History Tab */}
                    {tabValue === 0 && (
                        <>
                            {isUnlinked ? (
                                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={isMobile ? 150 : 300} gap={2} width="100%">
                                    <Typography variant="h6" color="text.secondary" textAlign="center">
                                        {data?.transaction?.type === 'provision'
                                            ? "This provision hasn't been linked to a transaction yet."
                                            : "This transaction hasn't been linked to a provision yet."}
                                    </Typography>
                                    <Button
                                        onClick={() => setOpenModal('link')}
                                        disabled={linkButtonDisabled}
                                        sx={{
                                            background: 'var(--color-page-background)',
                                            color: data?.transaction?.type === 'provision' ? 'var(--color-primary)' : 'var(--color-provision-text)',
                                            fontWeight: 600,
                                            fontSize: isMobile ? 12 : 14,
                                            height: isMobile ? 30 : 25,
                                            display: 'flex',
                                            gap: 1,
                                            px: isMobile ? 1 : 2,
                                            '&:hover': { backgroundColor: 'rgba(var(--color-primary-rgb), 0.0863)' },
                                            '&:disabled': { color: '#8c8c8cff' }
                                        }}
                                    >
                                        <Link size={isMobile ? 16 : 20} color={linkButtonDisabled ? "#8c8c8cff" : data?.transaction?.type === 'provision' ? "var(--color-primary)" : "var(--color-provision-text)"} />
                                        {data?.transaction?.type === 'provision' ? "Link this provision to a transaction" : "Link this transaction to a provision"}
                                    </Button>
                                </Box>
                            ) : (
                            <MaterialReactTable
                                key={data?.transaction?.type}
                                columns={tableColumns}
                                data={data ? (
                                    (() => {
                                        // Only reachable when isUnlinked is false, so `linked` is
                                        // always non-empty here - see the empty state above.
                                        const linked = (data?.linkedTransactions || []).filter((trx: any) => trx.guid !== data?.transaction?.guid);
                                        if (data?.transaction?.type === 'real' || isPaidAtImportProvision) {
                                            return [{ ...data.transaction, type: 'Imported' }, ...linked];
                                        } else {
                                            return [
                                                {
                                                    ...data.transaction,
                                                    type: 'Provision',
                                                    remainingBalance: (data?.linkedTransactions || []).find((trx: any) => trx.guid === data?.transaction?.guid)?.remainingBalance
                                                },
                                                ...linked
                                            ];
                                        }
                                    })()
                                ) : []}
                                enableRowVirtualization
                                enableColumnVirtualization
                                rowVirtualizerInstanceRef={rowVirtualizerInstanceRef}
                                rowVirtualizerOptions={{ overscan: 10 }}
                                columnVirtualizerOptions={{ overscan: 2 }}
                                enablePagination={false}
                                enableTopToolbar={false}
                                enableBottomToolbar={false}
                                enableColumnActions={false}
                                initialState={{ 
                                    density: 'compact',
                                    columnVisibility: {
                                        type: !data?.transaction?.type || data.transaction.type === 'real' || isPaidAtImportProvision,
                                        remainingBalance: data?.transaction?.type === 'provision' && !isPaidAtImportProvision,
                                    }
                                }}
                                muiTableContainerProps={{ sx: { height: isMobile ? 150 : 300, overflowY: 'auto' } }}
                                muiTablePaperProps={{ sx: { boxShadow: 'none' } }}
                                muiTableHeadCellProps={{ sx: { background: 'var(--color-container-background)' } }}
                                muiTableBodyRowProps={({ row, staticRowIndex }) => ({
                                    onClick: () => {
                                        const isMainTransaction = staticRowIndex === 0;
                                        const isLinkedTransaction = row.original.guid && row.original.guid !== transactionId;
                                        if (!isMainTransaction && isLinkedTransaction) {
                                            setSelectedLinkedTransactionId(row.original.guid);
                                        }
                                    },
                                    sx: {
                                        cursor: staticRowIndex !== 0 && row.original.guid && row.original.guid !== transactionId ? 'pointer' : 'default',
                                        '&:hover': staticRowIndex !== 0 && row.original.guid && row.original.guid !== transactionId ? { backgroundColor: 'var(--color-container-background)' } : {},
                                    }
                                })}
                            />
                            )}
                        </>
                    )}

                    {/* Related Tab */}
                    {tabValue === 1 && (
                        <>
                            {fetchRelated.isLoading ? (
                                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={isMobile ? 150 : 300} gap={2} width="100%">
                                    <CircularProgress size={40} />
                                    <Typography variant="h6" color="text.secondary">
                                        Loading related transactions...
                                    </Typography>
                                </Box>
                            ) : relatedTransactions.length === 0 ? (
                                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={isMobile ? 150 : 300} gap={2} width="100%">
                                    <Typography variant="h6" color="text.secondary">
                                        No related transactions found
                                    </Typography>
                                </Box>
                            ) : (
                                <MaterialReactTable
                                    key="related"
                                    columns={relatedTableColumns}
                                    data={relatedTransactions}
                                    enableRowVirtualization
                                    enableColumnVirtualization
                                    rowVirtualizerInstanceRef={relatedRowVirtualizerInstanceRef}
                                    rowVirtualizerOptions={{ overscan: 10 }}
                                    columnVirtualizerOptions={{ overscan: 2 }}
                                    enablePagination={false}
                                    enableTopToolbar={false}
                                    enableBottomToolbar={false}
                                    enableColumnActions={false}
                                    initialState={{
                                        density: 'compact',
                                        sorting: [{ id: 'date', desc: true }],
                                    }}
                                    muiTableContainerProps={{ sx: { height: isMobile ? 150 : 300, overflowY: 'auto' } }}
                                    muiTablePaperProps={{ sx: { boxShadow: 'none' } }}
                                    muiTableHeadCellProps={{ sx: { background: 'var(--color-container-background)' } }}
                                    muiTableBodyRowProps={({ row }) => ({
                                        onClick: () => {
                                            if (row.original.guid && row.original.guid !== transactionId) {
                                                setSelectedLinkedTransactionId(row.original.guid);
                                            }
                                        },
                                        sx: {
                                            cursor: row.original.guid && row.original.guid !== transactionId ? 'pointer' : 'default',
                                            '&:hover': row.original.guid && row.original.guid !== transactionId ? { backgroundColor: 'var(--color-container-background)' } : {},
                                        }
                                    })}
                                />
                            )}
                        </>
                    )}

                    {/* Pattern Analysis Tab (recurring provisions created via Provision This) */}
                    {tabValue === 2 && (
                        fetchPatternAnalysis.isLoading ? (
                            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={isMobile ? 150 : 300} gap={2} width="100%">
                                <CircularProgress size={40} />
                                <Typography variant="h6" color="text.secondary">
                                    Loading pattern analysis...
                                </Typography>
                            </Box>
                        ) : !patternAnalysis ? (
                            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={isMobile ? 150 : 300} gap={1} width="100%">
                                <Typography variant="h6" color="text.secondary">No pattern analysis recorded</Typography>
                                <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', textAlign: 'center', maxWidth: 320 }}>
                                    This provision wasn't created via "Provision This", so there's no source/matching transaction analysis to show.
                                </Typography>
                            </Box>
                        ) : (
                            <Box sx={{ maxHeight: isMobile ? 320 : 450, overflowY: 'auto' }}>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                                    <Box>
                                        <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Interval</Typography>
                                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{patternAnalysis.interval || '-'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Median Days Between Transactions</Typography>
                                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{patternAnalysis.medianDays ?? '-'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Matched Range (days)</Typography>
                                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                                            {patternAnalysis.matchedRange ? `${patternAnalysis.matchedRange[0]} - ${patternAnalysis.matchedRange[1]}` : '-'}
                                        </Typography>
                                    </Box>
                                    {patternAnalysis.usedTolerancePercent != null && (
                                        <Box>
                                            <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Tolerance Used</Typography>
                                            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{patternAnalysis.usedTolerancePercent}%</Typography>
                                        </Box>
                                    )}
                                </Box>
                                {patternAnalysisSourceTransaction && (
                                    <>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>Source Transaction</Typography>
                                        <MaterialReactTable
                                            columns={analysisColumns}
                                            data={[patternAnalysisSourceTransaction]}
                                            enableTopToolbar={false}
                                            enableBottomToolbar={false}
                                            enablePagination={false}
                                            enableColumnActions={false}
                                            enableSorting={false}
                                            initialState={{ density: 'compact' }}
                                            muiTablePaperProps={{ sx: { boxShadow: 'none', mb: 2 } }}
                                            muiTableHeadCellProps={{ sx: { background: 'var(--color-container-background)' } }}
                                        />
                                    </>
                                )}
                                <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>Matching Transactions</Typography>
                                {patternAnalysisMatches.length === 0 ? (
                                    <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>No matches were recorded.</Typography>
                                ) : (
                                    <MaterialReactTable
                                        columns={analysisColumns}
                                        data={patternAnalysisMatches}
                                        enableTopToolbar={false}
                                        enableBottomToolbar={false}
                                        enablePagination={false}
                                        enableColumnActions={false}
                                        initialState={{ density: 'compact', sorting: [{ id: 'date', desc: true }] }}
                                        muiTablePaperProps={{ sx: { boxShadow: 'none' } }}
                                        muiTableHeadCellProps={{ sx: { background: 'var(--color-container-background)' } }}
                                    />
                                )}
                            </Box>
                        )
                    )}

                    {/* Xero Payment Tab (a paid/partially-paid Xero invoice's
                    provision that FineAnts couldn't auto-link to a real
                    transaction - see sourceData.xeroPayments,
                    xero-import-service.js) - only rendered when the tab
                    itself is, so there's no separate empty state to handle. */}
                    {tabValue === 3 && data?.transaction?.type === 'provision' && (
                        <Box sx={{ maxHeight: isMobile ? 320 : 450, overflowY: 'auto' }}>
                            {(data?.transaction?.sourceData?.xeroPayments || []).map((p: any, i: number) => (
                                <Box
                                    key={p.paymentId || i}
                                    sx={{
                                        display: 'flex',
                                        gap: 4,
                                        py: 1.25,
                                        borderBottom: i < data.transaction.sourceData.xeroPayments.length - 1 ? '1px solid var(--color-container-background)' : 'none',
                                    }}
                                >
                                    <Box>
                                        <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Amount</Typography>
                                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{formatAmount?.(p.amount)}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Date</Typography>
                                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{formatDate(new Date(p.date))}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Reference</Typography>
                                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{p.reference || '-'}</Typography>
                                    </Box>
                                </Box>
                            ))}
                            <Divider sx={{ my: 1.5 }} />
                            {!isUnlinked ? (
                                <>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>Linked Transaction</Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {otherLinkedTransactions.map((trx: any) => (
                                            <Box
                                                key={trx.guid}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 4,
                                                    p: 1.25,
                                                    borderRadius: 1,
                                                    border: '1px solid rgba(26,127,55,0.3)',
                                                    background: 'rgba(26,127,55,0.08)',
                                                }}
                                            >
                                                <CheckCircle2 size={18} color="#1a7f37" style={{ flexShrink: 0 }} />
                                                <Box>
                                                    <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Amount</Typography>
                                                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{formatAmount?.(trx.amount)}</Typography>
                                                </Box>
                                                <Box>
                                                    <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Date</Typography>
                                                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{formatDate(new Date(trx.date))}</Typography>
                                                </Box>
                                                <Box>
                                                    <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Description</Typography>
                                                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{trx.description || '-'}</Typography>
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </>
                            ) : (
                            <>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>Matching Transactions</Typography>
                            {fetchRealCandidates.isLoading ? (
                                <Box display="flex" alignItems="center" gap={1} py={1}>
                                    <CircularProgress size={16} />
                                    <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Looking for matching transactions...</Typography>
                                </Box>
                            ) : fetchRealCandidates.error ? (
                                <Typography sx={{ fontSize: 12, color: '#d32f2f' }}>
                                    Failed to check for matching transactions: {fetchRealCandidates.error.toString()}
                                </Typography>
                            ) : !fetchRealCandidates.data?.data?.length ? (
                                <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                                    No unlinked transaction is dated the same day as a recorded payment.
                                </Typography>
                            ) : (() => {
                                const realCandidates = fetchRealCandidates.data.data;
                                const selectedGuids = realCandidates.filter((c: any) => realCandidateSelection[c.guid]).map((c: any) => c.guid);
                                const selectedTotal = realCandidates
                                    .filter((c: any) => realCandidateSelection[c.guid])
                                    .reduce((sum: number, c: any) => sum + Math.abs(Number(c.amount) || 0), 0);
                                const provisionAmount = Math.abs(Number(data?.transaction?.amount) || 0);
                                const isMatch = selectedGuids.length > 0 && Math.abs(selectedTotal - provisionAmount) < 0.005;
                                return (
                                    <>
                                        <MaterialReactTable
                                            columns={realCandidatesColumns}
                                            data={realCandidates}
                                            enableRowSelection
                                            getRowId={(row: any) => row.guid}
                                            state={{ rowSelection: realCandidateSelection }}
                                            onRowSelectionChange={setRealCandidateSelection}
                                            enableTopToolbar={false}
                                            enableBottomToolbar={false}
                                            enablePagination={false}
                                            enableColumnActions={false}
                                            initialState={{ density: 'compact', sorting: [{ id: 'date', desc: true }] }}
                                            muiTablePaperProps={{ sx: { boxShadow: 'none' } }}
                                            muiTableHeadCellProps={{ sx: { background: 'var(--color-container-background)' } }}
                                            muiTableBodyRowProps={({ row }) => ({
                                                onClick: row.getToggleSelectedHandler(),
                                                sx: { cursor: 'pointer' },
                                            })}
                                        />
                                        <Divider sx={{ my: 1.5 }} />
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                                            <Box>
                                                <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                                                    Selected total ({selectedGuids.length}): <Typography component="span" sx={{ fontSize: 13, fontWeight: 700, color: isMatch ? '#1a7f37' : 'inherit' }}>{formatAmount?.(selectedTotal)}</Typography>
                                                    {' '}/ Invoice amount: {formatAmount?.(provisionAmount)}
                                                </Typography>
                                                {selectedGuids.length > 0 && (
                                                    <Typography sx={{ fontSize: 11, color: isMatch ? '#1a7f37' : '#d32f2f' }}>
                                                        {isMatch ? 'Matches the invoice amount' : 'Does not match the invoice amount'}
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                disabled={selectedGuids.length === 0 || link.isLoading}
                                                startIcon={link.isLoading ? <CircularProgress size={14} color="inherit" /> : undefined}
                                                sx={{ color: '#fff', textTransform: 'none', flexShrink: 0 }}
                                                onClick={() => handleLinkTransactions(selectedGuids)}
                                            >
                                                {link.isLoading ? 'Linking…' : 'Link Selected'}
                                            </Button>
                                        </Box>
                                    </>
                                );
                            })()}
                            </>
                            )}
                        </Box>
                    )}

                    {/* Xero Tab (real transactions only) - provisions elsewhere
                    with an unmatched Xero payment dated the same day as this
                    transaction (see getXeroPaymentCandidates,
                    transaction-controller.js) - a ready-made linking
                    suggestion from the opposite direction of the tab above. */}
                    {tabValue === 3 && data?.transaction?.type === 'real' && (
                        fetchXeroCandidates.isLoading ? (
                            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={isMobile ? 150 : 300} gap={2} width="100%">
                                <CircularProgress size={40} />
                                <Typography variant="h6" color="text.secondary">Looking for matching Xero payments...</Typography>
                            </Box>
                        ) : fetchXeroCandidates.error ? (
                            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={isMobile ? 150 : 300} gap={1} width="100%">
                                <Typography variant="h6" color="error">Failed to check for matching Xero payments</Typography>
                                <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', textAlign: 'center', maxWidth: 360 }}>
                                    {fetchXeroCandidates.error.toString()}
                                </Typography>
                            </Box>
                        ) : !fetchXeroCandidates.data?.data?.length ? (
                            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={isMobile ? 150 : 300} gap={1} width="100%">
                                <Typography variant="h6" color="text.secondary">No matching Xero payments found</Typography>
                                <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', textAlign: 'center', maxWidth: 360 }}>
                                    No unlinked invoice has a Xero-recorded payment dated the same day as this transaction.
                                </Typography>
                            </Box>
                        ) : (() => {
                            const candidates = fetchXeroCandidates.data.data;
                            const selectedGuids = candidates.filter((c: any) => xeroCandidateSelection[c.guid]).map((c: any) => c.guid);
                            // matchedPayment.amount (what Xero actually paid on this invoice in
                            // THIS payment event) over the invoice's own amount - they diverge on
                            // a partial payment (e.g. $205 paid of a $240 invoice), and summing
                            // the invoice total there overcounts by the unpaid remainder.
                            const selectedTotal = candidates
                                .filter((c: any) => xeroCandidateSelection[c.guid])
                                .reduce((sum: number, c: any) => sum + Math.abs(Number(c.matchedPayment?.amount ?? c.amount) || 0), 0);
                            // The invoice's own amount, separately - shown alongside the Xero
                            // Payment total above only when they differ, so a partial payment
                            // within the selection (like Invoice 12434: $205 paid of $240) is
                            // visible instead of being silently absorbed into one blended number.
                            const selectedInvoiceTotal = candidates
                                .filter((c: any) => xeroCandidateSelection[c.guid])
                                .reduce((sum: number, c: any) => sum + Math.abs(Number(c.amount) || 0), 0);
                            const transactionAmount = Math.abs(Number(data?.transaction?.amount) || 0);
                            const isMatch = selectedGuids.length > 0 && Math.abs(selectedTotal - transactionAmount) < 0.005;
                            const invoiceTotalDiffers = selectedGuids.length > 0 && Math.abs(selectedInvoiceTotal - selectedTotal) >= 0.005;
                            return (
                                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                    <Box sx={{ maxHeight: isMobile ? 280 : 400, overflowY: 'auto' }}>
                                        <MaterialReactTable
                                            columns={xeroCandidatesColumns}
                                            data={candidates}
                                            enableRowSelection
                                            getRowId={(row: any) => row.guid}
                                            state={{ rowSelection: xeroCandidateSelection }}
                                            onRowSelectionChange={setXeroCandidateSelection}
                                            enableTopToolbar={false}
                                            enableBottomToolbar={false}
                                            enablePagination={false}
                                            enableColumnActions={false}
                                            enableSorting={false}
                                            initialState={{ density: 'compact' }}
                                            muiTablePaperProps={{ sx: { boxShadow: 'none' } }}
                                            muiTableHeadCellProps={{ sx: { background: 'var(--color-container-background)' } }}
                                            muiTableBodyRowProps={({ row }) => ({
                                                onClick: row.getToggleSelectedHandler(),
                                                sx: { cursor: 'pointer' },
                                            })}
                                        />
                                    </Box>
                                    <Divider sx={{ my: 1.5 }} />
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                                        <Box>
                                            <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                                                Selected Xero Payment total ({selectedGuids.length}): <Typography component="span" sx={{ fontSize: 13, fontWeight: 700, color: isMatch ? '#1a7f37' : 'inherit' }}>{formatAmount?.(selectedTotal)}</Typography>
                                                {' '}/ Invoice total: {formatAmount?.(selectedInvoiceTotal)}
                                                {' '}/ Transaction amount: {formatAmount?.(transactionAmount)}
                                            </Typography>
                                            {selectedGuids.length > 0 && (
                                                <Typography sx={{ fontSize: 11, color: isMatch ? '#1a7f37' : '#d32f2f' }}>
                                                    {isMatch ? 'Matches the transaction amount' : 'Does not match the transaction amount'}
                                                </Typography>
                                            )}
                                            {invoiceTotalDiffers && (
                                                <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                                                    Invoice total differs from the Xero payment total - one or more invoices in this selection were only partially paid
                                                </Typography>
                                            )}
                                        </Box>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            disabled={selectedGuids.length === 0 || link.isLoading}
                                            startIcon={link.isLoading ? <CircularProgress size={14} color="inherit" /> : undefined}
                                            sx={{ color: '#fff', textTransform: 'none', flexShrink: 0 }}
                                            onClick={() => handleLinkTransactions(selectedGuids)}
                                        >
                                            {link.isLoading ? 'Linking…' : 'Link Selected'}
                                        </Button>
                                    </Box>
                                </Box>
                            );
                        })()
                    )}
                </DialogContent>
            }
            <Divider sx={{ my: 1 }}/>
            <DialogActions sx={{ justifyContent: isMobile ? 'center' : 'space-between', alignItems: 'center', px: isMobile ? 1 : 3, pb: isMobile ? 1 : 2, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1 : 0, position: 'relative' }}>
                { data?.transaction?.type === 'real' && tabValue === 0 &&
                    <Box
                        sx={{
                            position: 'absolute',
                            top: -50,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: 300,
                            height: 40,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Typography fontSize={isMobile ? 14 : 16} fontWeight={600}>
                            Remaining Balance: {data?.transaction?.type === 'provision' ? formatAmount?.(data?.transaction?.amount) : formatAmount?.(data?.transaction?.amount - (data?.linkedTransactions || []).reduce((sum: number, trx: any) => sum + trx.amount, 0))}
                        </Typography>
                    </Box>
                }
                <Box display="flex" alignItems="center" gap={2}>
                    <TextToggle 
                        checked={isLinkable}
                        size="large"
                        onChange={onToggleStatus}
                        disabled={fetch.isLoading || !canChangeLinks}
                        text={!isLinkable ? "CLOSED" : "OPEN"}
                    />
                    {/* Moved into the History tab's empty state while the
                        provision is unlinked - there's nothing to click
                        through to here without first switching tabs, so
                        showing it in both places would be redundant. */}
                    {!isUnlinked &&
                    <Tooltip
                        title={linkTooltipTitle}
                        disableHoverListener={!linkTooltipTitle}
                        arrow
                        placement="top"
                        componentsProps={{
                            tooltip: {
                                sx: {
                                    backgroundColor: 'var(--color-page-background)',
                                    color: 'var(--color-default-text)',
                                    fontSize: 13,
                                    padding: '8px 16px',
                                    maxWidth: 300,
                                    boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)',
                                    '& .MuiTooltip-arrow': {
                                        color: '#fff',
                                    }
                                }
                            }
                        }}
                    >
                        <span>
                            <Button
                                onClick={() => setOpenModal('link')}
                                disabled={linkButtonDisabled}
                                fullWidth={isMobile}
                                sx={{
                                    background: 'var(--color-page-background)',
                                    color: data?.transaction?.type === 'provision' ? 'var(--color-primary)' : 'var(--color-provision-text)',
                                    fontWeight: 600,
                                    fontSize: isMobile ? 12 : 14,
                                    height: isMobile ? 30 : 25,
                                    display: 'flex',
                                    gap: 1,
                                    px: isMobile ? 1 : 2,
                                    '&:hover': { backgroundColor: 'rgba(var(--color-primary-rgb), 0.0863)' },
                                    '&:disabled': { color: '#8c8c8cff' }
                                }}>
                                <Link size={isMobile ? 16 : 20} color={linkButtonDisabled ? "#8c8c8cff" : data?.transaction?.type === 'provision' ? "var(--color-primary)" : "var(--color-provision-text)"} />
                                {isMobile ? (data?.transaction?.type === 'provision' ? "Link Trx" : "Link Prov") : (data?.transaction?.type === 'provision' ? "Link this provision to a transaction" : "Link this transaction to a provision")}
                            </Button>
                        </span>
                    </Tooltip>
                    }
                </Box>
                { data?.transaction?.type === 'real' &&
                    <Tooltip
                        title={provisionThisTooltipTitle}
                        disableHoverListener={!provisionThisTooltipTitle}
                        arrow
                        placement="top"
                        componentsProps={{
                            tooltip: {
                                sx: {
                                    backgroundColor: 'var(--color-page-background)',
                                    color: 'var(--color-default-text)',
                                    fontSize: 13,
                                    padding: '8px 16px',
                                    maxWidth: 300,
                                    boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)',
                                    '& .MuiTooltip-arrow': {
                                        color: '#fff',
                                    }
                                }
                            }
                        }}
                    >
                        <span>
                            <Button
                                onClick={() => {
                                    console.log('[ProvisionThis] "Provision This" clicked for', transactionId, '- state at click time:', { isDescriptionOnlyMatch, descriptionOptions });
                                    // Try to analyze pattern using API
                                    const { url, method } = buildApiRequestConfig(routes.provisionTemplate.analyzePattern, [{ param: 'transactionGuid', value: transactionId || '' }]);
                                    setAnalyzePattern({ url, method });

                                    // Set fallback data in case API fails
                                    setProvisionThisData({
                                        date: adjustStartDate(data.transaction.date),
                                        amount: data.transaction.amount,
                                        description: data.transaction.description
                                    });
                                }}
                                disabled={provisionThisDisabled}
                                variant="outlined"
                                color="primary"
                                fullWidth={isMobile}
                                sx={{
                                    borderRadius: 1,
                                    fontWeight: 600,
                                    fontSize: isMobile ? 12 : 14,
                                    py: isMobile ? 1 : 1.2,
                                    textTransform: 'none',
                                    height: isMobile ? 30 : 25,
                                    maxWidth: isMobile ? '100%' : 250,
                                }}>
                                Provision This
                            </Button>
                        </span>
                    </Tooltip>
                }
            </DialogActions>
        </Dialog>
    );
};

export default TransactionDetail;